import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { DateFormatPipe, MomentModule } from 'ngx-moment';
import { ItemDetails } from 'src/app/models/item-details';
import { UserIdToNamePipe } from 'src/app/shared/pipes/user-id-to-name.pipe';
import { ConfigService } from 'src/app/shared/services/config.service';

@Component({
  selector: 'app-workshop-jobs',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    RouterLink,
    MomentModule,
    TitleCasePipe,
    UserIdToNamePipe,
    AsyncPipe
  ],
  providers: [DateFormatPipe],
  template: `
    <div
      class="flex flex-col justify-start items-stretch gap-3 p-4 bg-white rounded-lg shadow-sm border border-slate-200"
    >
      <div
        class="flex flex-row justify-start items-center gap-x-3 pb-2 border-b border-slate-100"
      >
        <mat-icon class="text-amber-500">build</mat-icon>
        <h2 class="text-lg font-bold text-slate-800">Workshop Jobs</h2>
      </div>

      @if (hasCurrentJobs()) {
        <div
          class="flex flex-col gap-2 p-3 bg-amber-50 rounded-md hover:bg-amber-100 transition-colors"
        >
          <div class="flex flex-row items-center justify-between">
            <h3 class="font-medium text-slate-700">Current workshop jobs</h3>
            @if (currentWorkshopJobs().length > 1) {
              <span
                class="bg-amber-200 text-amber-800 text-xs font-medium px-2.5 py-0.5 rounded-full"
              >
                {{ currentWorkshopJobs().length }} active jobs
              </span>
            }
          </div>
          @for (job of currentWorkshopJobs(); track job.id) {
            <div
              class="flex flex-row items-center justify-between gap-2 p-2 rounded-md bg-white bg-opacity-50"
            >
              <div class="flex flex-col text-slate-700">
                <span class="font-medium">{{ job.type | titlecase }}</span>
                <span class="text-sm text-slate-500">
                  Booked in:
                  {{ job.booked_in_at | amDateFormat: dateTimeFormat }}
                </span>
              </div>
              <a
                [routerLink]="['/workshop/job', job.id]"
                mat-icon-button
                matTooltip="View job details"
                class="text-sm text-amber-600 hover:text-amber-800"
              >
                <mat-icon>link</mat-icon>
              </a>
            </div>
          }
        </div>
      }

      @if (hasLastService()) {
        <div class="flex flex-col gap-2 mt-2">
          <div class="flex flex-row items-center justify-between">
            <h3 class="text-sm font-medium text-slate-600">Last service</h3>
          </div>
          <div
            class="flex flex-row items-center justify-between gap-2 p-2 rounded-md hover:bg-slate-50 transition-colors"
          >
            <div class="flex flex-col text-slate-600">
              <p>
                The last service date was recorded as
                {{ lastService().value | amDateFormat: dateOnlyFormat }}
                by {{ lastService().added_by | userIdToName | async }}
              </p>
            </div>
            @if (lastService().promptable_type === 'workshopJob') {
              <a
                [routerLink]="['/workshop/job', lastService().promptable_id]"
                mat-icon-button
                matTooltip="View service details"
                class="text-sm text-slate-500 hover:text-amber-600"
              >
                <mat-icon>link</mat-icon>
              </a>
            }
          </div>
        </div>
      }

      @if (!hasCurrentJobs() || !hasLastService()) {
        <div class="flex items-center justify-center py-6">
          <p class="text-slate-500 italic flex items-center gap-2">
            <mat-icon class="text-slate-400">info</mat-icon>
            {{ incompleteWorkshopHistoryMessage() }}
          </p>
        </div>
      }
      <button
        mat-stroked-button
        color="primary"
        class="text-sm self-end hover:bg-slate-50 transition-colors mt-2"
        (click)="viewWorkshopHistory()"
      >
        <span class="flex items-center gap-1">
          View Full Workshop History
          <mat-icon class="text-sm">history</mat-icon>
        </span>
      </button>
    </div>
  `
})
export class WorkshopJobsComponent {
  itemDetails = input.required<ItemDetails>();
  dateTimeFormat = ConfigService.dates.shortDateTimeFormat;
  dateOnlyFormat = ConfigService.dates.dateFormat;
  goToWorkshopJob = output<void>();

  currentWorkshopJobs = computed(() => {
    return this.itemDetails().currentWorkshopJobs || [];
  });

  lastService = computed(() => {
    return this.itemDetails().lastService;
  });

  hasCurrentJobs = computed(() => {
    return this.currentWorkshopJobs().length > 0;
  });

  hasLastService = computed(() => {
    return this.lastService() !== null && this.lastService() !== undefined;
  });

  viewWorkshopHistory() {
    this.goToWorkshopJob.emit();
  }

  incompleteWorkshopHistoryMessage = computed(() => {
    if (!this.hasCurrentJobs() && !this.hasLastService()) {
      return 'No current workshop jobs or service history';
    }
    if (!this.hasCurrentJobs()) {
      return 'No current workshop jobs';
    }
    if (!this.hasLastService()) {
      return 'No last service date recorded';
    }
  });
}
